﻿import { LiveDataSetValue, LiveDataSet, Status } from "../../API/LiveData/LiveDataSet";
import { IUnitsConversionSet } from "../../Converters/UnitsConverter/IUnitsConverter";
import { ILiveDataListener } from "../../ILiveDataListener";
import { MainSite } from "../../MainSite";
import { SimpleDialog } from "../../SimpleDialog";
import { Profile, ProfileComponent } from "../../ValueTypes/Profile";
import { SpeedUnits } from "./Current";
import { ISiteObservationsPage } from "./ISiteObservationsPage";

class DateTimeInputValue {
    public isValid: boolean = false;
    public inputDate: string = "";
    public inputTime: string = "";
    public moment: moment.Moment = null;
}

interface ProfileChartOptions {
    xmin: number;
    xmax: number;
}

export enum ProfileTypeEnum {
    Depth,
    Height,
    Horizontal,
}

export class ProfileChart implements ILiveDataListener {
    private chart: JQuery;
    private stationId: string;
    private $toolbar: JQuery;
    private $messagebar: JQuery;
    private $spinner: JQuery;
    private $inputDate: JQuery;
    private $inputTime: JQuery;
    private $btnDTPrev: JQuery;
    private $btnDTNext: JQuery;
    private $chkUseLiveData: JQuery;

    private useLiveData: boolean = true; //: flag added when adding the date/time selector: if true, we're updating with each received livedata (liveDataChanged), otherwise we ignore the refresh and just use date/time provided in the inputs.
    private profileData: Profile = null;
    private timestamp: number = null;
    private requestTimestamp: number = null;
    public latestLiveDataValue: LiveDataSetValue = null; //: always store the latest value from LiveDataSet, as user may chose the use Live Data again at any point, and this would help avoid having to wait until page receives latest data again.
    public historicalValueKey: string = "P/{StationId}/{SensorId}/{ParameterId}"; // {Store}/{StationId}/{SensorId}/{ParameterId}

    constructor(
        private page: ISiteObservationsPage,
        container: JQuery,
        top: number,
        left: number,
        height: number,
        width: number,
        private speedUnits: SpeedUnits,
        private profileName: string,
        private profileEnumType: ProfileTypeEnum,
        private sensorId: string = null,
    ) {
        this.lsOptsKey = `${profileName}ProfileChartOpts`;

        this.stationId = page.station.Station.Id;
        window["_ProfileChart"] = this;

        var box: JQuery = $('<div class="midi_SiteObservations_chart_container"></div>')
            .css("position", "absolute")
            .css("top", top + "%")
            .css("left", left + "%")
            .css("height", height + "%")
            .css("width", width + "%");

        container.append(box);

        this.$toolbar = $("<div>").appendTo(box);
        this.$messagebar = $("<div>")
            .addClass("CurrentProfile--MessageBar text-right small")
            .css("height", "16px")
            .css("marginTop", "2px")
            .css("fontSize", "12px")
            .css("overflow", "hidden")
            .css("clear", "both")
            .appendTo(box);

        this.chart = $("<div></div>").css("height", "100%").css("clear", "both").appendTo(box);

        this.$spinner = $("<div>").addClass("CurrentProfile--Spinner text-muted").appendTo(box);
        this.$spinner.css({
            position: "absolute",
            top: "3px",
            left: "4px",
            zIndex: 100,
            padding: "4px",
            backgroundColor: "rbga(255,255,255,.9)",
        });
        $("<div>")
            .html('<i class="fa fa-spin fa-refresh"></i> <span>Loading</span>')
            .appendTo(this.$spinner);

        var currentProfileStyle = " float: right;";
        var windProfileStyle = " margin: auto;";
        let $dpWrap: JQuery = $(
            '<div class="input-group input-group-sm" style="max-width: 380px; ' +
                (this.profileEnumType === ProfileTypeEnum.Depth
                    ? currentProfileStyle
                    : windProfileStyle) +
                '"></div>',
        );
        $dpWrap.appendTo(this.$toolbar);

        this.$chkUseLiveData = $('<input type="checkbox" checked />').prependTo(
            $(
                '<span class="input-group-addon"><strong style="margin-left: 5px; display: inline-block;">Live Data</strong></span>',
            ).appendTo($dpWrap),
        );
        this.$chkUseLiveData.on("change", (e) => {
            this.setUseLiveData(this.$chkUseLiveData.is(":checked"));
        });

        // $('<input type="text" disabled readonly class="form-control" style="width:0; border: 0;">').appendTo($dpWrap); // an invisible input, for styling..

        this.$btnDTPrev = $("<button>")
            .html('<i class="fa fa-lg fa-fw fa-chevron-left"></i>')
            .addClass("btn btn-default")
            .css("borderRadius", 0)
            .appendTo($('<span class="input-group-btn">').appendTo($dpWrap));
        this.$inputDate = $("<input>")
            .addClass("form-control text-center")
            .attr("placeholder", "YYYY-MM-DD")
            .attr("type", "text")
            .appendTo($dpWrap);
        $(
            '<span class="input-group-addon" style="padding: 0; border: 0; width: 0;"></span>',
        ).appendTo($dpWrap);
        this.$inputTime = $("<input>")
            .addClass("form-control text-center")
            .attr("placeholder", "HH:MM:SS")
            .attr("type", "text")
            .appendTo($dpWrap);
        this.$btnDTNext = $("<button>")
            .html('<i class="fa fa-lg fa-fw fa-chevron-right"></i>')
            .addClass("btn btn-default")
            .appendTo($('<span class="input-group-btn">').appendTo($dpWrap));

        this.$inputDate.on("change", () => {
            this.dtOnChange();
        });
        this.$inputTime.on("change", () => {
            this.dtOnChange();
        });
        this.$inputDate.prop("disabled", true);
        this.$inputTime.prop("disabled", true);
        this.$btnDTPrev.prop("disabled", true);
        this.$btnDTNext.prop("disabled", true);

        let im = new Inputmask("99:99:99");
        im.mask(this.$inputTime[0]);

        this.$inputDate.datepicker(<DatepickerOptions>{
            //: https://bootstrap-datepicker.readthedocs.io/en/latest/options.html
            weekStart: 1,
            format: "yyyy-mm-dd",
            multidate: false,
            clearBtn: false,
            immediateUpdates: true,
            keepEmptyValues: true,
            todayBtn: true,
        });

        this.chart.css(
            "height",
            "calc(100% - " +
                Math.max(this.$toolbar.outerHeight() + this.$messagebar.outerHeight(), 48) +
                "px)",
        );

        this.drawChartMessage("Waiting for " + this.profileName + " Profile Data...", true);

        this.$btnDTNext.on("click", (e) => {
            $(e.target).blur();
            let newTs: number = moment
                .utc()
                .utcOffset(this.page.mainSite.getTimeZoneUtcOffset())
                .valueOf();
            let dtiv: DateTimeInputValue = this.getDtInputsValue();
            if (dtiv && dtiv.moment && dtiv.moment.isValid()) {
                newTs = dtiv.moment.valueOf();
            }

            newTs += 1000;
            this.getData(newTs, false, true);
        });

        this.$btnDTPrev.on("click", (e) => {
            $(e.target).blur();
            let newTs: number = moment
                .utc()
                .utcOffset(this.page.mainSite.getTimeZoneUtcOffset())
                .valueOf();
            let dtiv: DateTimeInputValue = this.getDtInputsValue();
            if (dtiv && dtiv.moment && dtiv.moment.isValid()) {
                newTs = dtiv.moment.valueOf();
            }

            newTs -= 1000;
            this.getData(newTs, true, true);
        });

        box.on("click", ".custom-actions", (e) => {
            e.preventDefault();
            e.stopPropagation();
            let $target: JQuery = $(e.currentTarget);
            $target.trigger("blur");
            let action = $target.data("action");

            try {
                //: wrapping in a UI silent try/catch just to prevent other failures in case of lack of browser support, although this should not cause any issues as support for following block is generally pretty good.
                if (
                    $target.hasClass("actions--set-xaxis-range") ||
                    $target[0].classList.contains("actions--set-xaxis-range")
                ) {
                    //: NB: .hasClass does not seem to work somehow.. SEE: https://developer.mozilla.org/en-US/docs/Web/API/Element/classList
                    action = "ProfileChart--set-xaxis-range";
                }
            } catch (err) {
                console.error("Error while attempting to determine .custom-actions class..", err);
            }

            switch (action) {
                default:
                    console.warn(`ERROR: Action '${action}' not recognised!`, {
                        e: e,
                        $target: $target,
                        action: action,
                    });
                    break;
                case "ProfileChart--set-xaxis-range":
                    this.showSpeedOptionsModal();
                    break;
            }
        });

        if (page.mainSite.liveDataManager.latestData) {
            this.liveDataChanged(page.mainSite.liveDataManager.latestData);
        }
    }

    public dtOnChange(): void {
        let dtiv: DateTimeInputValue = this.getDtInputsValue();

        if (dtiv.isValid) {
            this.getData(dtiv.moment.valueOf(), false);
        } else {
            this.setMessage(null);
            this.drawChartMessage("Date and/or Time indicated above not valid.");
        }
    }

    public getDtInputsValue(): DateTimeInputValue {
        let dtiv = new DateTimeInputValue();

        dtiv.inputDate = this.$inputDate.val();
        dtiv.inputTime = this.$inputTime.val();
        let m = moment.utc(
            dtiv.inputDate +
                " " +
                dtiv.inputTime +
                "" +
                this.page.mainSite.getTimeZoneStringFormat("ZZ"),
            "YYYY-MM-DD HH:mm:ssZ",
            true,
        );
        dtiv.isValid = m && m.isValid();
        if (dtiv.isValid) {
            dtiv.moment = m;
        }

        return dtiv;
    }

    private setUseLiveData(flag: boolean = true) {
        this.useLiveData = flag;

        if (this.useLiveData) {
            this.setMessage(null);

            this.$inputDate.prop("disabled", true);
            this.$inputTime.prop("disabled", true);
            this.$btnDTPrev.prop("disabled", true);
            this.$btnDTNext.prop("disabled", true);

            //: now set inputs/chart to latest available data:
            if (this.latestLiveDataValue == null) {
                this.$inputDate.val("");
                this.$inputTime.val("");

                this.drawChartMessage(this.profileName + " Profile not available");
            } else {
                this.setDateTimeInputValues(this.latestLiveDataValue.Timestamp);
                this.drawChart(
                    this.getProfileFromValue(this.latestLiveDataValue),
                    this.latestLiveDataValue.Timestamp,
                    null,
                );
            }
        } else {
            this.$inputDate.prop("disabled", false);
            this.$inputTime.prop("disabled", false);
            this.$btnDTPrev.prop("disabled", false);
            this.$btnDTNext.prop("disabled", false);

            //: do nothing else until user modifies the date/time manually?
        }
    }

    private setDateTimeInputValues(
        timestamp: number = null,
        triggerChange: boolean = false,
    ): void {
        this.$inputDate.val("");
        this.$inputTime.val("");

        if (timestamp !== null) {
            let m = moment.utc(timestamp);
            if (m.isValid()) {
                m.utcOffset(this.page.mainSite.getTimeZoneUtcOffset());

                this.$inputDate.val(m.format("YYYY-MM-DD"));
                this.$inputDate.datepicker("update");
                //this.$inputDate.datepicker.setUTCDate(m.toDate()); // ?..
                this.$inputTime.val(m.format("HH:mm:ss"));
            }
        }

        if (triggerChange) {
            this.$inputTime.trigger("change");
        }
    }

    public liveDataChanged(latestData: LiveDataSet): void {
        let groupName =
            this.profileEnumType === ProfileTypeEnum.Height
                ? "WindProfile_mps"
                : this.speedUnits == SpeedUnits.kts
                  ? this.profileName + "Profile/kts"
                  : this.profileName + "Profile";
        if (this.profileEnumType === ProfileTypeEnum.Horizontal) {
            groupName = "Horizontal" + groupName;
        }
        this.historicalValueKey = this.historicalValueKey
            .replace("{StationId}", this.stationId)
            .replace("{SensorId}", "Buoy")
            .replace("{ParameterId}", groupName);
        if (
            latestData.Stations[this.stationId] &&
            latestData.Stations[this.stationId].GroupValues[groupName] &&
            (latestData.Stations[this.stationId].GroupValues[groupName].filter(
                (v) => v.Sensor == this.sensorId,
            ).length > 0 ||
                !this.sensorId)
        ) {
            if (
                latestData.Stations[this.stationId].GroupValues[groupName].filter(
                    (v) => v.Sensor == this.sensorId,
                ).length > 0
            ) {
                // If the specified sensorId is available, use data from the specified sensorId
                this.latestLiveDataValue = latestData.Stations[this.stationId].GroupValues[
                    groupName
                ].filter((v) => v.Sensor == this.sensorId)[0];
            } else {
                // If sensorId is not specified, use data from the first available sensorId
                this.latestLiveDataValue =
                    latestData.Stations[this.stationId].GroupValues[groupName][0];
            }
        } else {
            // If the specified sensorId is not available
            this.latestLiveDataValue = null;
        }

        if (this.useLiveData) {
            if (this.latestLiveDataValue) {
                this.setMessage(null);
                this.setDateTimeInputValues(this.latestLiveDataValue.Timestamp);
                this.drawChart(
                    this.getProfileFromValue(this.latestLiveDataValue),
                    this.latestLiveDataValue.Timestamp,
                    null,
                );
            } else {
                this.setMessage(null);
                this.drawChartMessage(this.profileName + " Profile not available");
            }
        }
    }

    private getProfileFromValue(val: LiveDataSetValue): Profile {
        if (val == null || val.ExpiryStatus === null || val.ExpiryStatus === Status.Expired) {
            return null;
        }

        return <Profile>val.Value;
    }

    private getData(
        requestedTimestamp: number = null,
        isBackwardRequested: boolean = false,
        isPrevNextButton: boolean = false,
    ): void {
        this.setMessage(null);

        if (this.latestLiveDataValue == null) {
            console.warn(
                this.profileName +
                    " Profile Chart: Unable to run .getData() as we haven't yet received any LiveData, required to determine SensorId & ParameterId for the HistoricalData request.",
            );
            alert(
                "Unable to query data: please wait for the page to have received at least one Live Data Value before requesting a different timestamp.",
            );
        } else {
            if (requestedTimestamp == null) {
                let dtiv: DateTimeInputValue = this.getDtInputsValue();
                if (dtiv.isValid) {
                    requestedTimestamp = dtiv.moment.valueOf();
                } else {
                    requestedTimestamp = moment
                        .utc()
                        .utcOffset(this.page.mainSite.getTimeZoneUtcOffset())
                        .valueOf();
                }
            }
            let uri = `/api/v1/HistoricalData/P/${this.stationId}/${encodeURIComponent(this.latestLiveDataValue.Sensor)}/${encodeURIComponent(this.latestLiveDataValue.Parameter)}?limit=1&${isBackwardRequested ? "toUnixMillis" : "fromUnixMillis"}=${requestedTimestamp}`;

            // this.drawChartMessage("Retrieving Current Profile Data...", true);
            this.$spinner.show();

            $.ajax(uri + "", {
                cache: false,
                dataType: "json",
                type: "GET",
                timeout: 30000,
                success: (data: any, text: string, jqXHR: JQueryXHR) => {
                    //callback(null, data);
                    let profile: Profile = null;
                    let timestamp: number = null;

                    try {
                        if (
                            !Array.isArray(data.results[0].series[0].values) ||
                            data.results[0].series[0].values.length !== 1
                        ) {
                            console.warn(
                                "Server responded with none or more than 1 result, which is unexpected.",
                            );
                            if (isPrevNextButton) {
                                let typeWord: string = isBackwardRequested
                                    ? "previous"
                                    : "further";
                                this.drawChartMessage(
                                    `No ${typeWord} ${this.profileName} Profile available.`,
                                );
                            } else {
                                this.drawChartMessage(
                                    "Unable to find ${this.profileName} Profile Data for the requested Date and Time.",
                                );
                            }
                            return;
                        }

                        let m: moment.Moment = moment.utc(
                            data.results[0].series[0].values[0][0],
                            "YYYY-MM-DD HH:mm:ssZ",
                            true,
                        );

                        if (m && m.isValid()) {
                            profile = JSON.parse(
                                data.results[0].series[0].values[0][1],
                            ) as Profile;
                            timestamp = m.valueOf();

                            // console.info('CurrentProfileData', { profile: profile, ts: timestamp, m: m, serverResponse: data });

                            this.drawChart(profile, timestamp, requestedTimestamp);

                            if (timestamp !== requestedTimestamp) {
                                this.setDateTimeInputValues(timestamp, false);
                                if (!isPrevNextButton) {
                                    this.setMessage(
                                        "Using nearest data to " +
                                            moment
                                                .utc(requestedTimestamp)
                                                .utcOffset(
                                                    this.page.mainSite.getTimeZoneUtcOffset(),
                                                )
                                                .format("YYYY-MM-DD HH:mm:ss Z"),
                                    );
                                }
                            }
                        } else {
                            console.warn(
                                "Unable to parse " +
                                    this.profileName +
                                    " Profile Data from server response. Error: Timestamp not parseable to a valid Moment.",
                            );
                            this.drawChartMessage(
                                "Error while retrieving " + this.profileName + " Profile Data.",
                            );
                        }
                    } catch (err) {
                        console.warn(
                            "Unable to parse " +
                                this.profileName +
                                " Profile Data from server response. Error: ",
                            err,
                        );
                        this.drawChartMessage(
                            "Error while retrieving " + this.profileName + " Profile Data.",
                        );
                    }
                },
                error: (jqXHR: JQueryXHR, textStatus: string, errorThrown: string) => {
                    console.warn(
                        "Unable to parse " +
                            this.profileName +
                            " Profile Data from server response. Error: ",
                        errorThrown,
                    );
                    this.drawChartMessage(
                        "Error while retrieving " + this.profileName + " Profile Data.",
                    );
                },
            });
        }
    }

    private setMessage(
        msg: string = null,
        className: string = "text-muted",
        icon: string = "fa fa-fw fa-info-circle",
    ): void {
        this.$messagebar.empty();

        if (msg) {
            let $div = $("<div>").addClass(className).append($("<span>").text(msg));
            if (icon) {
                $div.prepend($("<i>").addClass(icon));
            }

            $div.appendTo(this.$messagebar);
        }
    }

    private drawChartMessage(
        msg: string = this.profileName + " Profile not available",
        isLoading: boolean = false,
    ): void {
        this.chart.empty();
        let $div: JQuery = $("<div>").addClass("midi_SiteObservations_chart_no_data").text(msg);
        if (isLoading) {
            $div.prepend('<i class="fa fa-fw fa-refresh fa-spin" style="margin-right: 5px;"></i>');
        }
        this.chart.append($div);

        this.$spinner.hide();
    }

    private drawChart(
        profile: Profile = null,
        timestamp: number,
        requestTimestamp: number = null,
    ): void {
        var speedUnitsStr = this.speedUnits == SpeedUnits.mps ? "m/s" : "kts";
        var unitsStrDepthOrHeight = "m";

        this.timestamp = timestamp;
        this.requestTimestamp = requestTimestamp;
        this.profileData = profile;

        this.chart.empty();

        if (profile == null) {
            this.chart.append(
                $(
                    '<div class="midi_SiteObservations_chart_no_data">' +
                        this.profileName +
                        " Profile not available</div>",
                ),
            );
            this.$spinner.hide();
        } else {
            var seriesOptions = [];

            var upperXAxisData = [];
            var lowerXAxisData = [];

            //: get unitConversionSet's and factors (ucf_ prefixed vars = "unit conversion factor"):
            let unitsConversionSet_DepthOrHeight: IUnitsConversionSet | null =
                MainSite.getUnitConverter().getConversionSet("m");
            let unitsConversionSet_Speed: IUnitsConversionSet | null =
                MainSite.getUnitConverter().getConversionSet(speedUnitsStr);
            let ucf_DepthOrHeight: (value: any) => any = (value: any) => value;
            let ucf_Speed: (value: any) => any = (value: any) => value;
            if (unitsConversionSet_DepthOrHeight) {
                ucf_DepthOrHeight = unitsConversionSet_DepthOrHeight.convertValue;
                unitsStrDepthOrHeight = unitsConversionSet_DepthOrHeight.convertedUnits;
            }
            if (unitsConversionSet_Speed) {
                ucf_Speed = unitsConversionSet_Speed.convertValue;
                speedUnitsStr = unitsConversionSet_Speed.convertedUnits;
            }

            profile.Components.forEach((component: ProfileComponent) => {
                if (this.profileEnumType === ProfileTypeEnum.Depth && component.Depth) {
                    if (component.Direction) {
                        upperXAxisData.push([
                            component.Direction,
                            ucf_DepthOrHeight(component.Depth),
                        ]);
                    }

                    if (component.Speed) {
                        lowerXAxisData.push([
                            ucf_Speed(component.Speed),
                            ucf_DepthOrHeight(component.Depth),
                        ]);
                    }
                }
                if (this.profileEnumType === ProfileTypeEnum.Height && component.Height) {
                    if (component.Direction) {
                        upperXAxisData.push([
                            component.Direction,
                            ucf_DepthOrHeight(component.Height),
                        ]);
                    }

                    if (component.Speed) {
                        lowerXAxisData.push([
                            ucf_Speed(component.Speed),
                            ucf_DepthOrHeight(component.Height),
                        ]);
                    }
                }

                if (
                    this.profileEnumType === ProfileTypeEnum.Horizontal &&
                    ucf_DepthOrHeight(component.Depth)
                ) {
                    if (component.Direction) {
                        upperXAxisData.push([
                            ucf_DepthOrHeight(component.Depth),
                            component.Direction,
                        ]);
                    }

                    if (component.Speed) {
                        lowerXAxisData.push([
                            ucf_DepthOrHeight(component.Depth),
                            ucf_Speed(component.Speed),
                        ]);
                    }
                }
            });

            var xAxisOptions: __Highcharts.AxisOptions[] = [];
            var yAxisOptions: __Highcharts.AxisOptions[] = [];

            seriesOptions.push(
                {
                    name: "Speed",
                    color: "red",
                    data: lowerXAxisData,
                    xAxis: 0,
                    yAxis: 0,
                    dashStyle: "longdash",
                    tooltip: {
                        headerFormat: "<b>{series.name}</b><br>",
                        pointFormatter:
                            this.profileEnumType === ProfileTypeEnum.Horizontal
                                ? function () {
                                      return (
                                          sprintf("%.1f", this.y) +
                                          " " +
                                          speedUnitsStr +
                                          " at " +
                                          sprintf("%.1f", this.x) +
                                          " " +
                                          unitsStrDepthOrHeight
                                      );
                                  }
                                : function () {
                                      return (
                                          sprintf("%.1f", this.x) +
                                          " " +
                                          speedUnitsStr +
                                          " at " +
                                          sprintf("%.1f", this.y) +
                                          " " +
                                          unitsStrDepthOrHeight
                                      );
                                  },
                    },
                },
                {
                    name: "Direction " + (this.profileName == "Current" ? "To" : "From"),
                    color: "green",
                    data: upperXAxisData,
                    xAxis: this.profileEnumType === ProfileTypeEnum.Horizontal ? 0 : 1,
                    yAxis: this.profileEnumType === ProfileTypeEnum.Horizontal ? 1 : 0,
                    lineWidth: 0,
                    marker: {
                        enabled: true,
                    },
                    tooltip: {
                        headerFormat: "<b>{series.name}</b><br>",
                        pointFormatter:
                            this.profileEnumType === ProfileTypeEnum.Horizontal
                                ? function () {
                                      return (
                                          sprintf("%.0f", this.y) +
                                          " °T " +
                                          (this.profileName == "Current" ? "TO" : "FROM") +
                                          " at " +
                                          sprintf("%.1f", this.x) +
                                          " " +
                                          unitsStrDepthOrHeight
                                      );
                                  }
                                : function () {
                                      return (
                                          sprintf("%.0f", this.x) +
                                          " °T " +
                                          (this.profileName == "Current" ? "TO" : "FROM") +
                                          " at " +
                                          sprintf("%.1f", this.y) +
                                          " " +
                                          unitsStrDepthOrHeight
                                      );
                                  },
                    },
                },
            );

            let speedAxisOptions: any = {
                title: {
                    text: `Speed (${speedUnitsStr}) <span class="custom-actions actions--set-xaxis-range" style="color: blue; text-decoration: underline; cursor: pointer;">Custom Range</span>`,
                    useHtml: true,
                },
                alignTicks: false,
            };

            let _opts: ProfileChartOptions = this.lsOptsGet();
            if (
                typeof _opts == "object" &&
                _opts !== null &&
                "xmin" in _opts &&
                !isNaN(_opts.xmin) &&
                _opts.xmin !== null
            ) {
                speedAxisOptions.min = _opts.xmin;
            }
            if (
                typeof _opts == "object" &&
                _opts !== null &&
                "xmax" in _opts &&
                !isNaN(_opts.xmax) &&
                _opts.xmax !== null
            ) {
                speedAxisOptions.max = _opts.xmax;
            }

            let dirAxisOptions = {
                title: {
                    text: "Direction (°T " + (this.profileName == "Current" ? "TO" : "FROM") + ")",
                },
                min: 0,
                max: 360,
                opposite: true,
                tickInterval: 90,
                minTickInterval: 90,
                alignTicks: false,
            };

            var depthAxisOptions = {
                title: {
                    text: "Depth (" + unitsStrDepthOrHeight + ")",
                },
                reversed: true,
            };

            var heightAxisOptions = {
                title: {
                    text: "Height (" + unitsStrDepthOrHeight + ")",
                },
                reversed: false,
            };

            var distanceAxisOptions = {
                title: {
                    text: "Distance (" + unitsStrDepthOrHeight + ")",
                },
                reversed: false,
            };

            if (this.profileEnumType !== ProfileTypeEnum.Horizontal) {
                xAxisOptions.push(speedAxisOptions);
                xAxisOptions.push(dirAxisOptions);
                yAxisOptions.push(
                    this.profileEnumType === ProfileTypeEnum.Depth
                        ? depthAxisOptions
                        : heightAxisOptions,
                );
            } else {
                xAxisOptions.push(distanceAxisOptions);
                yAxisOptions.push(speedAxisOptions);
                yAxisOptions.push(dirAxisOptions);
            }

            this.chart.highcharts({
                chart: {
                    type: "line",
                    marginTop: 60,
                },
                exporting: {
                    enabled: true,
                },
                title: {
                    text: "",
                },
                xAxis: xAxisOptions,
                yAxis: yAxisOptions,
                legend: {
                    enabled: true,
                },
                plotOptions: {
                    line: {
                        marker: {
                            radius: 5,
                            enabled: true,
                        },
                    },
                    series: {
                        animation: false,
                        // Line series default to 'x' only, which picks the wrong point on vertical profiles.
                        findNearestPointBy: "xy",
                    } as any,
                },
                series: seriesOptions,
                credits: {
                    enabled: false,
                },
            });

            this.$spinner.hide();
        }
    }

    /**
     * A helper method to just redraw the chart with exact same current data.
     * Added when providing ability to modify chart scales.
     */
    private refreshProfileChart(): void {
        this.drawChart(this.profileData, this.timestamp, this.requestTimestamp);
    }

    ////
    private lsOptsKey: string; //: the key/name for cookie or local storage for user options for ProfileChart.

    private lsOptsGet(): ProfileChartOptions {
        let x: ProfileChartOptions = {
            xmin: 0,
            xmax: null,
        };

        let ls: string = $.cookie(this.lsOptsKey);
        if (ls) {
            try {
                let parsed: ProfileChartOptions = JSON.parse(ls) as ProfileChartOptions;
                if (parsed) {
                    x = parsed;
                }
            } catch (err) {
                console.warn(
                    "Unable to parse locally stored " +
                        this.profileName +
                        "ProfileChart Options..",
                    { err: err, ls: ls },
                );
            }
        }

        return x;
    }

    private lsOptsSet(x: ProfileChartOptions, redrawChart: boolean = true): void {
        let s: string = JSON.stringify(x);
        $.cookie(this.lsOptsKey, s, { expires: 5 * 365, path: "/" });

        if (redrawChart) {
            this.refreshProfileChart();
        }
    }

    private showSpeedOptionsModal() {
        let opts: ProfileChartOptions = this.lsOptsGet();
        let sd = new SimpleDialog(this.profileName + ` Profile Chart Speed Range`, null);
        let $body: JQuery = sd.getBody();
        let $foot: JQuery = sd.getFooter();

        $body.empty();

        let $form: JQuery = $("<form>").addClass("form-horizontal").appendTo($body);
        let $group: JQuery = null;
        let $check_label: JQuery = null;
        let $inputMinVal: JQuery = null;
        let $inputMaxVal: JQuery = null;
        let $inputMinCheck: JQuery = null;
        let $inputMaxCheck: JQuery = null;

        if (1 == 1) {
            //: code wrapper for inputs setup..
            //: min:
            $group = $("<div>").addClass("form-group").appendTo($form);
            $("<label>").addClass("col-sm-2 control-label").text("Minimum:").appendTo($group);
            $inputMinVal = $(
                '<input type="text" style="text-align:right;" name="min" placeholder="Minimum">',
            ).addClass("input-sm form-control");
            $inputMinVal.appendTo($('<div class="col-sm-5">').appendTo($group));
            $check_label = $("<label>")
                .addClass("checkbox inline")
                .appendTo($('<div class="col-sm-5">').appendTo($group));
            $inputMinCheck = $('<input type="checkbox" name="min_check">').appendTo($check_label);
            $check_label.append("<span>Auto</span>");

            $inputMinCheck.on("change", () => {
                if ($inputMinCheck.is(":checked")) {
                    $inputMinVal.val("").prop("disabled", true);
                } else {
                    $inputMinVal.prop("disabled", false);
                }
            });

            //: max:
            $group = $("<div>").addClass("form-group").appendTo($form);
            $("<label>").addClass("col-sm-2 control-label").text("Maximum:").appendTo($group);
            $inputMaxVal = $(
                '<input type="text" style="text-align:right;" name="max" placeholder="Maximum">',
            ).addClass("input-sm form-control");
            $inputMaxVal.appendTo($('<div class="col-sm-5">').appendTo($group));
            $check_label = $("<label>")
                .addClass("checkbox inline")
                .appendTo($('<div class="col-sm-5">').appendTo($group));
            $inputMaxCheck = $('<input type="checkbox" name="max_check">').appendTo($check_label);
            $check_label.append("<span>Auto</span>");

            $inputMaxCheck.on("change", () => {
                if ($inputMaxCheck.is(":checked")) {
                    $inputMaxVal.val("").prop("disabled", true);
                } else {
                    $inputMaxVal.prop("disabled", false);
                }
            });

            //: set values of inputs:
            $inputMinCheck.prop("checked", true).trigger("change");
            $inputMaxCheck.prop("checked", true).trigger("change");
            if (opts !== null && "xmin" in opts && opts.xmin !== null) {
                $inputMinCheck.prop("checked", false).trigger("change");
                $inputMinVal.val(opts.xmin.toString());
            }
            if (opts !== null && "xmax" in opts && opts.xmax !== null) {
                $inputMaxCheck.prop("checked", false).trigger("change");
                $inputMaxVal.val(opts.xmax.toString());
            }
        }

        sd.getDismissButton().text("Cancel");

        let $save: JQuery = $("<button>")
            .addClass("btn btn-success")
            .text("Save")
            .prependTo($foot);
        $save.on("click", (e) => {
            let x: ProfileChartOptions = {
                xmin: null,
                xmax: null,
            };

            if (
                $inputMinCheck.prop("checked") == false &&
                !isNaN(parseFloat($inputMinVal.val()))
            ) {
                x.xmin = parseFloat($inputMinVal.val());
            }

            if (
                $inputMaxCheck.prop("checked") == false &&
                !isNaN(parseFloat($inputMaxVal.val()))
            ) {
                x.xmax = parseFloat($inputMaxVal.val());
            }

            this.lsOptsSet(x, true);

            sd.hide();
        });

        let $reset: JQuery = $("<button>")
            .addClass("btn btn-link pull-left")
            .text("Reset")
            .prependTo($foot);
        $reset.on("click", (e) => {
            $.removeCookie(this.lsOptsKey);
            sd.hide();
            this.refreshProfileChart();
        });

        sd.show();
    }
}
